import { Component, ElementRef, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService, showToast } from './api.service';
import { jobStatusClass } from './job-status-style';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-admin-customers',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="p-4 md:p-5 min-h-full bg-[#F7FAFC] overflow-y-auto">
      <div class="max-w-[1500px] mx-auto">
        @if (!selectedCustomer()) {
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 shrink-0">
            <div>
              <h1 class="text-xl font-semibold text-slate-900 tracking-tight">Customers</h1>
              <p class="text-slate-500 text-xs">Manage customer records, job history, and contact details.</p>
            </div>
            <button (click)="openCreateModal()" class="h-10 px-4 text-white rounded-lg text-xs font-semibold shadow-sm transition-opacity hover:opacity-90 flex items-center justify-center gap-2 cursor-pointer" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
              <i class="ti ti-user-plus text-base"></i> New Customer
            </button>
          </div>

          <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Total Records</p>
              <p class="text-xl font-semibold text-slate-900 mt-1">{{ customers().length }}</p>
            </div>
            <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Saved Customers</p>
              <p class="text-xl font-semibold text-[#1A6FA6] mt-1">{{ savedCustomerCount() }}</p>
            </div>
            <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Job History Only</p>
              <p class="text-xl font-semibold text-amber-700 mt-1">{{ historyOnlyCount() }}</p>
            </div>
            <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Total Jobs</p>
              <p class="text-xl font-semibold text-slate-900 mt-1">{{ totalJobsCount() }}</p>
            </div>
          </div>

          <div class="bg-white rounded-xl shadow-sm overflow-hidden border border-[#E7EEF5]">
            <div class="px-4 py-3 flex items-center gap-3 flex-wrap border-b border-slate-100">
              <div class="relative flex-1 min-w-full sm:min-w-[220px] max-w-sm">
                <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[15px]"></i>
                <input type="text" placeholder="Search customers..." [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)"
                       class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:outline-none focus:border-[#2B9AD2] placeholder:text-slate-400">
              </div>
              <select [ngModel]="sourceFilter()" (ngModelChange)="sourceFilter.set($event)" class="h-9 px-3 rounded-lg text-xs border border-slate-200 bg-slate-50 text-slate-600 focus:outline-none cursor-pointer">
                <option value="">All Sources</option>
                <option value="database">Saved Customers</option>
                <option value="job_history">Job History Only</option>
              </select>
            </div>

            <div class="md:hidden p-3 space-y-3 bg-slate-50/70">
              @if (isLoading()) {
                <div class="bg-white rounded-xl border border-slate-100 px-4 py-8 text-center text-sm text-slate-400">Loading customers...</div>
              } @else {
                @for (customer of filteredCustomers(); track customer.id) {
                  <button type="button" (click)="selectCustomer(customer)" class="w-full bg-white rounded-xl border border-[#E7EEF5] p-3 text-left shadow-sm">
                    <div class="flex items-start gap-3">
                      <div class="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-xs shrink-0" style="background:linear-gradient(135deg,#2B9AD2,#5EC9F0);">
                        {{ customerInitial(customer) }}
                      </div>
                      <div class="min-w-0 flex-1">
                        <div class="flex items-start justify-between gap-2">
                          <div class="min-w-0">
                            <p class="font-semibold text-slate-900 text-sm truncate">{{ customer.name || 'Unnamed customer' }}</p>
                            <p class="text-[10px] text-slate-400 mt-0.5">{{ customer.phone || 'No phone' }}</p>
                          </div>
                          <span class="px-2 py-0.5 rounded-md text-[9px] font-bold border uppercase tracking-wide shrink-0" [ngClass]="sourceBadgeClass(customer)">
                            {{ customer.source === 'job_history' ? 'History' : 'Saved' }}
                          </span>
                        </div>
                        <p class="text-xs text-slate-500 truncate mt-2">{{ customer.address || 'No address recorded' }}</p>
                        <div class="grid grid-cols-2 gap-2 mt-3">
                          <div>
                            <p class="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Jobs</p>
                            <p class="text-xs font-semibold text-slate-900">{{ customer.job_count || 0 }}</p>
                          </div>
                          <div>
                            <p class="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Accepted</p>
                            <p class="text-xs font-semibold text-slate-900">{{ customer.total_spent || 0 | currency:'NZD':'symbol':'1.2-2' }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                } @empty {
                  <div class="bg-white rounded-xl border border-slate-100 px-4 py-10 text-center text-sm text-slate-400">No customers found.</div>
                }
              }
            </div>

            <div class="hidden md:block overflow-x-auto">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr class="bg-slate-50">
                    <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Customer</th>
                    <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Contact</th>
                    <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Address</th>
                    <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center">Jobs</th>
                    <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-right">Accepted Quotes</th>
                    <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center">Source</th>
                  </tr>
                </thead>
                <tbody>
                  @if (isLoading()) {
                    <tr><td colspan="6" class="px-4 py-8 text-center text-sm text-slate-400">Loading customers...</td></tr>
                  } @else {
                    @for (customer of filteredCustomers(); track customer.id) {
                      <tr class="hover:bg-slate-50 transition-colors cursor-pointer border-t border-slate-100" (click)="selectCustomer(customer)">
                        <td class="px-4 py-3">
                          <div class="flex items-center gap-3">
                            <div class="w-9 h-9 rounded-lg flex items-center justify-center text-white font-semibold text-xs shrink-0" style="background:linear-gradient(135deg,#2B9AD2,#5EC9F0);">
                              {{ customerInitial(customer) }}
                            </div>
                            <div class="min-w-0">
                              <p class="font-semibold text-slate-900 text-sm truncate">{{ customer.name || 'Unnamed customer' }}</p>
                              <p class="text-[10px] text-slate-400">ID: {{ customer.id }}</p>
                            </div>
                          </div>
                        </td>
                        <td class="px-4 py-3">
                          <p class="text-xs font-semibold text-slate-700">{{ customer.phone || 'No phone' }}</p>
                          <p class="text-[10px] text-slate-400">{{ customer.email || 'No email' }}</p>
                        </td>
                        <td class="px-4 py-3 text-xs text-slate-600 max-w-[320px] truncate">{{ customer.address || 'No address recorded' }}</td>
                        <td class="px-4 py-3 text-center font-semibold text-slate-900">{{ customer.job_count || 0 }}</td>
                        <td class="px-4 py-3 text-right font-semibold text-slate-900">{{ customer.total_spent || 0 | currency:'NZD':'symbol':'1.2-2' }}</td>
                        <td class="px-4 py-3 text-center">
                          <span class="px-2.5 py-1 rounded-md text-[10px] font-bold border uppercase tracking-wide" [ngClass]="sourceBadgeClass(customer)">
                            {{ customer.source === 'job_history' ? 'History' : 'Saved' }}
                          </span>
                        </td>
                      </tr>
                    } @empty {
                      <tr><td colspan="6" class="px-4 py-10 text-center text-sm text-slate-400">No customers found.</td></tr>
                    }
                  }
                </tbody>
              </table>
            </div>
          </div>
        } @else {
          @if (selectedCustomer(); as customer) {
            <div class="max-w-6xl mx-auto">
              <button (click)="selectCustomer(null)" class="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#1A6FA6] font-semibold text-xs mb-4 transition-colors cursor-pointer">
                <i class="ti ti-arrow-left"></i> Customers
              </button>

              <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-5 mb-5">
                <div class="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div>
                    <div class="flex flex-wrap items-center gap-2">
                      <h1 class="text-xl font-semibold text-slate-950 tracking-tight">{{ customer.name }}</h1>
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-bold border uppercase tracking-wide" [ngClass]="sourceBadgeClass(customer)">
                        {{ customer.source === 'job_history' ? 'Job history only' : 'Saved customer' }}
                      </span>
                    </div>
                    <p class="text-sm text-slate-500 mt-1">{{ customer.address || 'No address recorded' }}</p>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    @if (isSavedCustomer(customer)) {
                      <button (click)="openEditModal()" class="h-9 px-3 bg-white border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg shadow-sm hover:bg-slate-50 transition-colors cursor-pointer flex items-center gap-2">
                        <i class="ti ti-pencil"></i> Edit
                      </button>
                      <button (click)="deleteCustomer(customer.id)" class="h-9 px-3 bg-white border border-rose-200 text-rose-600 text-xs font-semibold rounded-lg shadow-sm hover:bg-rose-50 transition-colors cursor-pointer flex items-center gap-2">
                        <i class="ti ti-trash"></i> Delete
                      </button>
                    }
                  </div>
                </div>

                <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5 pt-5 border-t border-slate-100">
                  <div>
                    <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Phone</p>
                    <p class="text-sm font-semibold text-slate-900 mt-1">{{ customer.phone || 'Not recorded' }}</p>
                  </div>
                  <div>
                    <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Email</p>
                    <p class="text-sm font-semibold text-slate-900 mt-1 truncate">{{ customer.email || 'Not recorded' }}</p>
                  </div>
                  <div>
                    <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Jobs</p>
                    <p class="text-sm font-semibold text-slate-900 mt-1">{{ customer.job_count || customer.jobs?.length || 0 }}</p>
                  </div>
                  <div>
                    <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Accepted Quotes</p>
                    <p class="text-sm font-semibold text-slate-900 mt-1">{{ customer.total_spent || 0 | currency:'NZD':'symbol':'1.2-2' }}</p>
                  </div>
                </div>
              </div>

              @if (customer.notes) {
                <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-5 mb-5">
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Notes</p>
                  <p class="text-sm text-slate-700 leading-relaxed">{{ customer.notes }}</p>
                </div>
              }

              <div class="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
                  <div class="px-5 py-4 border-b border-slate-100">
                    <h2 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">Job History</h2>
                  </div>
                  <div class="divide-y divide-slate-100">
                    @for (job of customer.jobs || []; track job.id) {
                      <div class="px-5 py-4 flex items-center justify-between gap-3">
                        <div>
                          <p class="text-sm font-semibold text-slate-900">{{ job.id }}</p>
                          <p class="text-xs text-slate-500">{{ job.type || 'Job' }} - {{ job.date ? (job.date | date:'d MMM y') : 'No schedule date' }}</p>
                        </div>
                        <span class="px-2.5 py-1 rounded-md text-[10px] font-bold border uppercase tracking-wide" [ngClass]="jobStatusClass(job.status)">
                          {{ job.status || 'Unknown' }}
                        </span>
                      </div>
                    } @empty {
                      <div class="px-5 py-8 text-center text-sm text-slate-400">No job history found.</div>
                    }
                  </div>
                </section>

                <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
                  <div class="px-5 py-4 border-b border-slate-100">
                    <h2 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">Quotes / Commercial History</h2>
                  </div>
                  <div class="divide-y divide-slate-100">
                    @for (payment of customer.payments || []; track payment.id) {
                      <div class="px-5 py-4 flex items-center justify-between gap-3">
                        <div>
                          <p class="text-sm font-semibold text-slate-900">{{ payment.quote_no || payment.id }}</p>
                          <p class="text-xs text-slate-500">{{ payment.date ? (payment.date | date:'d MMM y') : 'No date' }}</p>
                        </div>
                        <div class="text-right">
                          <p class="text-sm font-semibold text-slate-900">{{ payment.amount || 0 | currency:'NZD':'symbol':'1.2-2' }}</p>
                          <p class="text-[10px] font-bold uppercase tracking-wide text-slate-400">{{ payment.status }}</p>
                        </div>
                      </div>
                    } @empty {
                      <div class="px-5 py-8 text-center text-sm text-slate-400">No quote history found.</div>
                    }
                  </div>
                </section>

                <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden xl:col-span-2">
                  <div class="px-5 py-4 border-b border-slate-100">
                    <h2 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">Customer Feedback</h2>
                    <p class="text-xs text-slate-500 mt-1">Ratings and service follow-up linked to completed work.</p>
                  </div>
                  <div class="divide-y divide-slate-100">
                    @for (item of customer.feedback || []; track item.id) {
                      <div class="px-5 py-4 flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                        <div class="min-w-0">
                          <div class="flex flex-wrap items-center gap-2">
                            <p class="text-sm font-semibold text-slate-900">{{ item.model_type }} {{ item.reference_id }}</p>
                            <span class="px-2.5 py-1 rounded-md text-[10px] font-bold border uppercase tracking-wide" [ngClass]="feedbackStatusClass(item.status)">
                              {{ item.status || 'Requested' }}
                            </span>
                          </div>
                          <p class="text-xs text-slate-500 mt-1">
                            {{ item.service_category || 'General feedback' }}
                            @if (item.submitted_at) { <span> - {{ item.submitted_at | date:'d MMM y, h:mm a' }}</span> }
                          </p>
                          @if (item.comments) {
                            <p class="text-sm text-slate-700 leading-relaxed mt-2">{{ item.comments }}</p>
                          }
                          @if (item.follow_up_notes) {
                            <p class="text-xs text-slate-500 mt-2"><strong>Follow-up:</strong> {{ item.follow_up_notes }}</p>
                          }
                          <div class="flex flex-wrap gap-2 mt-3">
                            @if (item.status === 'Needs Follow-up') {
                              <button type="button" (click)="updateFeedbackStatus(item, 'Resolved')" class="h-8 px-3 rounded-lg bg-white border border-emerald-200 text-emerald-700 text-[11px] font-semibold hover:bg-emerald-50">
                                Mark Resolved
                              </button>
                            }
                            @if (item.testimonial_permission == 1 && item.status !== 'Approved Testimonial') {
                              <button type="button" (click)="updateFeedbackStatus(item, 'Approved Testimonial')" class="h-8 px-3 rounded-lg bg-white border border-sky-200 text-sky-700 text-[11px] font-semibold hover:bg-sky-50">
                                Approve Testimonial
                              </button>
                            }
                          </div>
                        </div>
                        <div class="text-left lg:text-right shrink-0">
                          <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Rating</p>
                          <p class="text-xl font-black text-slate-900">{{ item.rating || '-' }}/5</p>
                          @if (item.testimonial_permission == 1) {
                            <p class="text-[10px] font-bold uppercase tracking-wide text-emerald-600">Testimonial allowed</p>
                          }
                        </div>
                      </div>
                    } @empty {
                      <div class="px-5 py-8 text-center text-sm text-slate-400">No customer feedback recorded yet.</div>
                    }
                  </div>
                </section>
              </div>
            </div>
          }
        }

        @if (isModalOpen()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-slate-900">{{ editingCustomer() ? 'Edit Customer' : 'New Customer' }}</h2>
                  <p class="text-xs text-slate-500 mt-1">Keep customer contact details clean for jobs, projects, and quotes.</p>
                </div>
                <button (click)="closeModal()" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500 cursor-pointer">
                  <i class="ti ti-x text-lg"></i>
                </button>
              </div>

              <div class="p-5 overflow-y-auto space-y-5">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label class="block">
                    <span class="field-label">Full Name</span>
                    <input type="text" [(ngModel)]="customerForm.name" class="field-input">
                  </label>
                  <label class="block">
                    <span class="field-label">Phone</span>
                    <input type="text" [(ngModel)]="customerForm.phone" class="field-input">
                  </label>
                  <label class="block">
                    <span class="field-label">Email</span>
                    <input type="email" [(ngModel)]="customerForm.email" class="field-input">
                  </label>
                  <label class="block">
                    <span class="field-label">Rating</span>
                    <select [(ngModel)]="customerForm.rating" class="field-input bg-white">
                      <option [ngValue]="0">Not rated</option>
                      <option [ngValue]="1">1 star</option>
                      <option [ngValue]="2">2 stars</option>
                      <option [ngValue]="3">3 stars</option>
                      <option [ngValue]="4">4 stars</option>
                      <option [ngValue]="5">5 stars</option>
                    </select>
                  </label>
                  <label class="block md:col-span-2">
                    <span class="field-label">Address</span>
                    <input #customerAddressInput type="text" [(ngModel)]="customerForm.address" (ngModelChange)="onAddressManualChange()" class="field-input">
                    @if (mapsKey()) {
                      <p class="text-[10px] font-semibold text-slate-400 mt-1">Select an Auckland Google Maps address when available, or type the address manually.</p>
                    }
                  </label>
                  <label class="block md:col-span-2">
                    <span class="field-label">Tags</span>
                    <input type="text" [(ngModel)]="tagInput" placeholder="VIP, Body Corp, Property Manager" class="field-input">
                  </label>
                  <label class="block md:col-span-2">
                    <span class="field-label">Notes</span>
                    <textarea [(ngModel)]="customerForm.notes" rows="4" class="field-input h-auto py-2 resize-none"></textarea>
                  </label>
                </div>
              </div>

              <div class="p-5 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                <button type="button" (click)="closeModal()" class="px-4 py-2 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-200 cursor-pointer">Cancel</button>
                <button (click)="saveCustomer()" [disabled]="isSaving() || !customerForm.name.trim()" class="px-5 py-2 bg-[#1A6FA6] text-white text-sm font-semibold rounded-lg hover:bg-[#0C3A5A] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center gap-2">
                  @if (isSaving()) { <i class="ti ti-loader animate-spin"></i> Saving... } @else { {{ editingCustomer() ? 'Save Changes' : 'Create Customer' }} }
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .field-label {
      display: block;
      margin-bottom: .375rem;
      font-size: .75rem;
      font-weight: 700;
      color: #475569;
    }
    .field-input {
      width: 100%;
      height: 2.5rem;
      border-radius: .5rem;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      padding: 0 .75rem;
      font-size: .875rem;
      color: #1e293b;
      outline: none;
    }
    .field-input:focus {
      border-color: #2B9AD2;
      background: #fff;
    }
  `]
})
export class AdminCustomersComponent implements OnInit {
  @ViewChild('customerAddressInput') customerAddressInput?: ElementRef<HTMLInputElement>;

  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private pendingCustomerId: string | null = null;

  searchQuery = signal('');
  sourceFilter = signal('');
  selectedCustomer = signal<any | null>(null);
  customers = signal<any[]>([]);
  isLoading = signal(false);
  isSaving = signal(false);
  isModalOpen = signal(false);
  editingCustomer = signal<any | null>(null);
  mapsKey = signal('');
  mapsLoaded = signal(false);
  tagInput = '';
  customerForm = this.emptyCustomerForm();

  savedCustomerCount = computed(() => this.customers().filter(c => c.source !== 'job_history').length);
  historyOnlyCount = computed(() => this.customers().filter(c => c.source === 'job_history').length);
  totalJobsCount = computed(() => this.customers().reduce((sum, c) => sum + Number(c.job_count || 0), 0));

  filteredCustomers = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const source = this.sourceFilter();
    return this.customers().filter(customer => {
      const queryMatches = !query || [
        customer.name,
        customer.email,
        customer.phone,
        customer.address
      ].some(value => String(value || '').toLowerCase().includes(query));
      const sourceMatches = !source || customer.source === source;
      return queryMatches && sourceMatches;
    });
  });

  ngOnInit() {
    this.route.queryParamMap.subscribe(params => {
      const query = params.get('q') || '';
      const customerId = params.get('customer_id');
      if (query) this.searchQuery.set(query);
      this.pendingCustomerId = customerId;
      this.openPendingCustomer();
    });
    this.refreshCustomers();
    this.loadMapSettings();
  }

  refreshCustomers() {
    this.isLoading.set(true);
    this.api.getCustomers().subscribe({
      next: (res: any) => {
        this.customers.set(res.success ? (res.data?.customers || []) : []);
        this.isLoading.set(false);
        this.openPendingCustomer();
      },
      error: () => {
        this.isLoading.set(false);
        showToast('error', 'Failed to load customers.');
      }
    });
  }

  selectCustomer(customer: any | null) {
    if (!customer) {
      this.selectedCustomer.set(null);
      return;
    }
    this.isLoading.set(true);
    this.api.getCustomer(customer.id).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success) {
          this.selectedCustomer.set(res.data);
        } else {
          showToast('error', res.message || 'Failed to load customer.');
        }
      },
      error: () => {
        this.isLoading.set(false);
        showToast('error', 'Failed to load customer.');
      }
    });
  }

  private openPendingCustomer(): void {
    if (!this.pendingCustomerId || this.customers().length === 0) return;
    const match = this.customers().find(customer => String(customer.id) === String(this.pendingCustomerId));
    if (!match) return;
    this.pendingCustomerId = null;
    this.selectCustomer(match);
  }

  openCreateModal() {
    this.editingCustomer.set(null);
    this.customerForm = this.emptyCustomerForm();
    this.tagInput = '';
    this.isModalOpen.set(true);
    setTimeout(() => this.initAddressAutocomplete(), 0);
  }

  openEditModal() {
    const customer = this.selectedCustomer();
    if (!customer || !this.isSavedCustomer(customer)) return;
    this.editingCustomer.set(customer);
    this.customerForm = {
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      address_place_id: customer.address_place_id || '',
      latitude: customer.latitude !== undefined && customer.latitude !== null ? Number(customer.latitude) : null,
      longitude: customer.longitude !== undefined && customer.longitude !== null ? Number(customer.longitude) : null,
      notes: customer.notes || '',
      rating: Number(customer.rating || 0),
      tags: Array.isArray(customer.tags) ? customer.tags : []
    };
    this.tagInput = this.customerForm.tags.join(', ');
    this.isModalOpen.set(true);
    setTimeout(() => this.initAddressAutocomplete(), 0);
  }

  closeModal() {
    this.isModalOpen.set(false);
    this.editingCustomer.set(null);
  }

  saveCustomer() {
    if (this.isSaving() || !this.customerForm.name.trim()) return;
    this.isSaving.set(true);

    const payload = {
      ...this.customerForm,
      name: this.customerForm.name.trim(),
      email: this.customerForm.email.trim(),
      phone: this.customerForm.phone.trim(),
      address: this.customerForm.address.trim(),
      address_place_id: this.customerForm.address_place_id,
      latitude: this.customerForm.latitude,
      longitude: this.customerForm.longitude,
      tags: this.parseTags(this.tagInput)
    };
    const request = this.editingCustomer()
      ? this.api.updateCustomer({ id: this.editingCustomer().id, ...payload })
      : this.api.createCustomer(payload);

    request.subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', this.editingCustomer() ? 'Customer updated.' : 'Customer created.');
          const editedId = this.editingCustomer()?.id;
          this.closeModal();
          this.refreshCustomers();
          if (editedId) this.selectCustomer({ id: editedId });
        } else {
          showToast('error', res.message || 'Failed to save customer.');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Network error while saving customer.');
      }
    });
  }

  deleteCustomer(id: number) {
    if (!confirm('Delete this customer record? Job history will remain, but this saved customer profile will be removed.')) return;
    this.api.deleteCustomer(id).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Customer deleted.');
          this.selectedCustomer.set(null);
          this.refreshCustomers();
        } else {
          showToast('error', res.message || 'Failed to delete customer.');
        }
      },
      error: () => showToast('error', 'Network error while deleting customer.')
    });
  }

  isSavedCustomer(customer: any): boolean {
    return customer?.source !== 'job_history' && !String(customer?.id || '').startsWith('ORG-');
  }

  customerInitial(customer: any): string {
    return String(customer?.name || 'C').trim().charAt(0).toUpperCase();
  }

  sourceBadgeClass(customer: any): string {
    return customer?.source === 'job_history'
      ? 'bg-amber-50 text-amber-700 border-amber-100'
      : 'bg-emerald-50 text-emerald-700 border-emerald-100';
  }

  feedbackStatusClass(status: string): string {
    if (status === 'Needs Follow-up') return 'bg-rose-50 text-rose-700 border-rose-100';
    if (status === 'Resolved' || status === 'Approved Testimonial') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (status === 'Submitted') return 'bg-sky-50 text-sky-700 border-sky-100';
    return 'bg-amber-50 text-amber-700 border-amber-100';
  }

  updateFeedbackStatus(item: any, status: string) {
    if (!item?.id) return;
    this.api.updateCustomerFeedbackStatus({ id: item.id, status }).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Feedback status updated.');
          const selected = this.selectedCustomer();
          if (selected) this.selectCustomer(selected);
        } else {
          showToast('error', res.message || 'Feedback status could not be updated.');
        }
      },
      error: (err: any) => showToast('error', err?.error?.message || 'Feedback status could not be updated.')
    });
  }

  jobStatusClass(status: string): string {
    return jobStatusClass(status);
  }

  private parseTags(value: string): string[] {
    return value.split(',').map(tag => tag.trim()).filter(Boolean);
  }

  private emptyCustomerForm() {
    return {
      name: '',
      email: '',
      phone: '',
      address: '',
      address_place_id: '',
      latitude: null as number | null,
      longitude: null as number | null,
      notes: '',
      rating: 0,
      tags: [] as string[]
    };
  }

  onAddressManualChange() {
    this.customerForm.address_place_id = '';
    this.customerForm.latitude = null;
    this.customerForm.longitude = null;
  }

  private loadMapSettings() {
    this.api.getCompanySettings().subscribe({
      next: (res: any) => {
        const key = String(res?.data?.google_maps_browser_api_key || '').trim();
        this.mapsKey.set(key);
        if (key) this.loadGoogleMapsScript(key);
      }
    });
  }

  private loadGoogleMapsScript(key: string) {
    if (typeof window === 'undefined') return;
    if ((window as any).google?.maps?.places) {
      this.mapsLoaded.set(true);
      this.initAddressAutocomplete();
      return;
    }
    const existing = document.getElementById('google-maps-js');
    if (existing) {
      existing.addEventListener('load', () => {
        this.mapsLoaded.set(true);
        this.initAddressAutocomplete();
      });
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.mapsLoaded.set(true);
      this.initAddressAutocomplete();
    };
    document.head.appendChild(script);
  }

  private initAddressAutocomplete() {
    if (!this.customerAddressInput?.nativeElement || !(window as any).google?.maps?.places) return;
    const google = (window as any).google;
    const autocomplete = new google.maps.places.Autocomplete(this.customerAddressInput.nativeElement, {
      componentRestrictions: { country: 'nz' },
      bounds: this.aucklandBounds(),
      strictBounds: true,
      fields: ['formatted_address', 'geometry', 'place_id']
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const lat = place.geometry?.location ? Number(place.geometry.location.lat()) : null;
      const lng = place.geometry?.location ? Number(place.geometry.location.lng()) : null;
      if (!this.isInAucklandBounds(lat, lng)) {
        this.customerForm.address_place_id = '';
        this.customerForm.latitude = null;
        this.customerForm.longitude = null;
        showToast('error', 'Please select an Auckland address.');
        return;
      }
      this.customerForm.address = place.formatted_address || this.customerForm.address;
      this.customerForm.address_place_id = place.place_id || '';
      this.customerForm.latitude = lat;
      this.customerForm.longitude = lng;
    });
  }

  private aucklandBounds() {
    return {
      north: -36.35,
      south: -37.35,
      east: 175.45,
      west: 174.15
    };
  }

  private isInAucklandBounds(lat: number | null, lng: number | null): boolean {
    if (lat === null || lng === null) return false;
    const bounds = this.aucklandBounds();
    return lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
  }
}
